import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { NewRequestModal } from '@/components/requests/new-request-modal'
import { RequestDetailModal } from '@/components/requests/request-detail-modal'
import { useServiceRequests } from '@/hooks/use-requests'
import { formatDate } from '@/lib/utils'
import type { ServiceRequestStatus } from '@riverpark/types'

const STATUS_BADGE: Record<
  ServiceRequestStatus,
  { variant: 'pending' | 'committee' | 'paid' | 'draft'; label: string }
> = {
  open: { variant: 'pending', label: 'Open' },
  in_progress: { variant: 'committee', label: 'In progress' },
  resolved: { variant: 'paid', label: 'Resolved' },
  closed: { variant: 'draft', label: 'Closed' },
}

const TYPE_LABEL: Record<string, string> = {
  maintenance: 'Maintenance',
  billing_query: 'Billing query',
  complaint: 'Complaint',
  general: 'General',
}

export function MemberRequests() {
  const [searchParams] = useSearchParams()
  const { data, isLoading } = useServiceRequests({ mine: true })
  const [composing, setComposing] = useState(false)
  const [selected, setSelected] = useState<string | null>(null)
  const items = data?.data ?? []

  // Depends on the "open" param value (not just mount) so clicking a notification while already
  // on this page — a same-pathname navigation React Router won't remount for — still opens it.
  useEffect(() => {
    const openId = searchParams.get('open')
    if (openId) setSelected(openId)
  }, [searchParams])

  return (
    <PageShell>
      <PageHeader
        label="Support"
        title="Service Requests"
        description="Submit maintenance, billing, or estate enquiries."
        action={
          <Button size="sm" onClick={() => setComposing(true)}>
            <Plus size={14} className="mr-1.5" />
            New request
          </Button>
        }
      />

      {isLoading ? (
        <p className="font-sans text-sm text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <p className="rounded border border-border-2 px-4 py-6 text-center font-sans text-sm text-muted-foreground">
          You haven't submitted any requests yet.
        </p>
      ) : (
        <div className="space-y-2">
          {items.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setSelected(r.id)}
              className="flex w-full items-center justify-between gap-3 rounded border border-border-2 bg-card p-3 text-left transition-colors hover:bg-cream-2/50"
            >
              <div>
                <p className="font-sans text-sm font-medium text-ink">{r.subject}</p>
                <p className="mt-0.5 font-sans text-xs text-muted-foreground">
                  {TYPE_LABEL[r.type]} · {formatDate(new Date(r.createdAt * 1000))}
                </p>
              </div>
              <Badge variant={STATUS_BADGE[r.status].variant}>{STATUS_BADGE[r.status].label}</Badge>
            </button>
          ))}
        </div>
      )}

      {composing && <NewRequestModal onClose={() => setComposing(false)} />}
      {selected && (
        <RequestDetailModal
          requestId={selected}
          canChangeStatus={false}
          onClose={() => setSelected(null)}
        />
      )}
    </PageShell>
  )
}
